import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material';
import TimeSheetApi from '@infrastructure/timeSheet-api';
import { getErrorPayload } from '@application/common/helpers/api-helper';
const messageFor = (failure, mutation) => {
    const error = getErrorPayload(failure);
    if (error.status === 401)
        return 'Your session expired. Sign in again before removing hours.';
    if (error.status === 403)
        return 'You can only remove your own open timesheets.';
    if (error.status === 404)
        return 'This timesheet is no longer available. Refresh the overview.';
    if (error.code === 'TIME_SHEET_DISCARD_PROTECTED')
        return 'This timesheet has submission or processing history and cannot be removed. Contact your administrator.';
    if (error.code === 'TIME_SHEET_DISCARD_CHANGED')
        return 'These hours changed after the preview. Close this dialog and review the timesheet again.';
    return mutation
        ? 'Removal could not be confirmed. Reload the page to check the timesheet before trying again.'
        : 'The removal preview could not be loaded. Close this dialog and try again.';
};
export const DiscardOpenTimeSheet = ({ timeSheetId, onClose, onRemoved, onBusyChange }) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [needsReload, setNeedsReload] = useState(false);
    const inFlight = useRef(false);
    useEffect(() => {
        if (!timeSheetId)
            return;
        let current = true;
        const abort = new AbortController();
        setPreview(null);
        setError('');
        setNeedsReload(false);
        setLoading(true);
        TimeSheetApi.previewDiscard(timeSheetId, abort.signal)
            .then(response => { if (current)
            setPreview(response.data); })
            .catch(failure => { if (current)
            setError(messageFor(failure, false)); })
            .finally(() => { if (current)
            setLoading(false); });
        return () => { current = false; abort.abort(); };
    }, [timeSheetId]);
    const remove = async () => {
        if (!preview || inFlight.current || error)
            return;
        inFlight.current = true;
        setSaving(true);
        onBusyChange?.(true);
        let removed = false;
        try {
            await TimeSheetApi.discardOpen(preview.timeSheetId, preview.version);
            removed = true;
            await onRemoved();
            onClose();
        }
        catch (failure) {
            setError(removed ? 'The timesheet was removed, but the overview could not refresh. Reload the page.' : messageFor(failure, true));
            const status = getErrorPayload(failure).status;
            setNeedsReload(removed || !status || status >= 500 || getErrorPayload(failure).code === 'TIME_SHEET_DISCARD_OUTCOME_UNCERTAIN');
        }
        finally {
            inFlight.current = false;
            setSaving(false);
            onBusyChange?.(false);
        }
    };
    return _jsxs(Dialog, { open: !!timeSheetId, onClose: () => { if (!inFlight.current)
            onClose(); }, fullWidth: true, maxWidth: 'sm', "aria-labelledby": 'discard-timesheet-title', "aria-describedby": preview ? 'discard-timesheet-description' : undefined, children: [_jsx(DialogTitle, { id: 'discard-timesheet-title', children: "Remove open timesheet?" }), _jsxs(DialogContent, { children: [loading && _jsx(Typography, { role: 'status', children: "Checking whether this timesheet can be removed\u2026" }), error && _jsx(Alert, { severity: 'error', sx: { mb: 2 }, children: error }), preview && _jsxs(_Fragment, { children: [_jsxs(Typography, { fontWeight: 600, sx: { mb: 1, overflowWrap: 'anywhere' }, children: [new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(new Date(preview.year, preview.month - 1, 1)), " \u00B7 ", preview.contextName] }), _jsxs(DialogContentText, { id: 'discard-timesheet-description', children: ["This removes all ", preview.totalHours, " saved hours across ", preview.weekCount, " ", preview.weekCount === 1 ? 'week' : 'weeks', ' ', "for this month and registration from your overview. Other months and registrations are unchanged. You cannot undo this yourself. Contact an administrator if you need recovery."] })] })] }), _jsxs(DialogActions, { sx: { px: 3, pb: 2, flexWrap: 'wrap', gap: 1, '& button': { minHeight: 44 } }, children: [_jsx(Button, { autoFocus: true, onClick: onClose, disabled: saving, children: error ? 'Close' : 'Keep timesheet' }), needsReload ? _jsx(Button, { variant: 'contained', onClick: () => window.location.reload(), children: "Reload page" })
                        : _jsx(Button, { variant: 'contained', color: 'error', onClick: () => void remove(), disabled: loading || saving || !preview || !!error, children: saving ? 'Removing…' : 'Remove timesheet' })] })] });
};
